import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import useStyles from "./style";
import { productFeatures } from "Pages/ProgrammeMPlus/mplusUtils";
import MplusApi from "Services/Mplus";
import {
  VEHICLE_STATUS,
  formatDateTime,
  formatMoney,
  getErrorMessage,
  locationName,
  missingLabel,
  statusLabel,
} from "./constants";

const INFO_FIELDS = [
  { key: "vin", label: "N° de châssis (VIN)", upper: true },
  { key: "plate", label: "Immatriculation", upper: true },
  { key: "color", label: "Couleur" },
  { key: "supplier_name", label: "Fournisseur" },
  { key: "purchase_date", label: "Date d'achat", type: "date" },
  { key: "purchase_price", label: "Prix d'achat (XAF)", type: "number", cost: true },
  { key: "freight_cost", label: "Transport (XAF)", type: "number", cost: true },
  { key: "duty_cost", label: "Douane (XAF)", type: "number", cost: true },
  { key: "clearing_cost", label: "Dédouanement (XAF)", type: "number", cost: true },
];

const DOCUMENT_FIELDS = [
  { key: "registration_number", label: "N° de carte grise" },
  { key: "registration_date", label: "Date de carte grise", type: "date" },
  { key: "insurance_policy", label: "N° de police d'assurance" },
  { key: "insurance_expiry", label: "Fin de l'assurance", type: "date" },
  { key: "gps_device_id", label: "ID du boîtier GPS" },
  { key: "lewootrack_unit_id", label: "ID unité LewooTrack", type: "number" },
];

const toInputValue = (value, type) => {
  if (value === null || value === undefined) return "";
  return type === "date" ? String(value).slice(0, 10) : String(value);
};

// Une valeur peut être à la racine du véhicule ou dans un sous-objet (costs, documents).
const readField = (vehicle, key, group) => vehicle?.[group]?.[key] ?? vehicle?.[key];

const buildPayload = (fields, values, initial) =>
  fields.reduce((payload, field) => {
    const value = values[field.key];
    if (value === initial[field.key]) return payload;
    if (field.type === "number") payload[field.key] = value === "" ? null : Number(value);
    else payload[field.key] = value === "" ? null : field.upper ? value.trim().toUpperCase() : value.trim();
    return payload;
  }, {});

const Detail = ({ label, children }) => {
  const classes = useStyles();
  return (
    <Box className={classes.detailItem}>
      <Typography className={classes.detailLabel}>{label}</Typography>
      <Typography className={classes.detailValue} component="div">{children}</Typography>
    </Box>
  );
};

const VehicleDetailsDialog = ({ vehicleId, onClose, onChanged, notify }) => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState({});
  const [infoInitial, setInfoInitial] = useState({});
  const [documents, setDocuments] = useState({});
  const [documentsInitial, setDocumentsInitial] = useState({});
  const [pdiPassed, setPdiPassed] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadVehicle = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      const response = await MplusApi.getVehicle(vehicleId);
      const data = response?.data?.data || null;
      const nextInfo = INFO_FIELDS.reduce((values, field) => ({
        ...values,
        [field.key]: toInputValue(readField(data, field.key, field.cost ? "costs" : undefined), field.type),
      }), {});
      const nextDocuments = DOCUMENT_FIELDS.reduce((values, field) => ({
        ...values,
        [field.key]: toInputValue(readField(data, field.key, "documents"), field.type),
      }), {});
      setVehicle(data);
      setInfo(nextInfo);
      setInfoInitial(nextInfo);
      setDocuments(nextDocuments);
      setDocumentsInitial(nextDocuments);
      setPdiPassed(Boolean(readField(data, "pdi_passed", "documents")));
    } catch (error) {
      notify(getErrorMessage(error, "Impossible de charger la fiche du véhicule."), "error");
    } finally {
      setLoading(false);
    }
  }, [notify, vehicleId]);

  const loadHistory = useCallback(async () => {
    if (!vehicleId) return;
    setHistoryLoading(true);
    try {
      const response = await MplusApi.getVehicleHistory(vehicleId);
      setHistory(response?.data?.data?.movements || []);
    } catch (error) {
      setHistory([]);
      notify(getErrorMessage(error, "Impossible de charger l'historique."), "error");
    } finally {
      setHistoryLoading(false);
    }
  }, [notify, vehicleId]);

  useEffect(() => {
    setTab(0);
    setVehicle(null);
    setHistory([]);
    loadVehicle();
  }, [loadVehicle]);

  useEffect(() => {
    if (tab === 2) loadHistory();
  }, [loadHistory, tab]);

  if (!vehicleId) return null;

  const save = async (request, successMessage) => {
    setSaving(true);
    try {
      await request();
      notify(successMessage, "success");
      await loadVehicle();
      onChanged();
    } catch (error) {
      notify(getErrorMessage(error, "Enregistrement impossible."), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInfo = (event) => {
    event.preventDefault();
    const payload = buildPayload(INFO_FIELDS, info, infoInitial);
    if (!Object.keys(payload).length) return;
    save(() => MplusApi.updateVehicle(vehicleId, payload), "Informations enregistrées.");
  };

  const handleSaveDocuments = (event) => {
    event.preventDefault();
    const payload = buildPayload(DOCUMENT_FIELDS, documents, documentsInitial);
    if (pdiPassed !== Boolean(readField(vehicle, "pdi_passed", "documents"))) payload.pdi_passed = pdiPassed;
    if (!Object.keys(payload).length) return;
    save(() => MplusApi.updateVehicleDocuments(vehicleId, payload), "Contrôles enregistrés.");
  };

  const renderField = (field, values, setValues) => (
    <Box className={classes.fieldGroup} key={field.key}>
      <Typography className={classes.fieldLabel}>{field.label}</Typography>
      <TextField
        fullWidth
        size="small"
        type={field.type || "text"}
        inputProps={field.type === "number" ? { min: 0 } : undefined}
        value={values[field.key] ?? ""}
        onChange={(event) => setValues((previous) => ({ ...previous, [field.key]: event.target.value }))}
      />
    </Box>
  );

  const status = VEHICLE_STATUS[vehicle?.status];
  const missing = vehicle?.readiness?.missing || [];

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" className={classes.entryDialog}>
      <DialogTitle className={classes.entryDialogTitle}>
        <Box>
          <Typography className={classes.dialogTitleText}>{vehicle?.product_catalog?.name || "Fiche véhicule"}</Typography>
          <Typography className={classes.dialogSubtitle}>{[vehicle?.vin || vehicle?.plate, ...productFeatures(vehicle?.product_catalog)].filter(Boolean).join(" · ")}</Typography>
        </Box>
        <IconButton aria-label="Fermer" onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      <Box className={classes.detailTabsWrap}>
        <Tabs value={tab} onChange={(event, value) => setTab(value)} className={classes.detailTabs}>
          <Tab label="Informations" />
          <Tab label="Contrôles avant vente" />
          <Tab label="Historique" />
        </Tabs>
      </Box>
      <DialogContent className={classes.entryDialogContent}>
        {loading && !vehicle ? (
          <Box className={classes.dialogLoader}><CircularProgress size={28} /></Box>
        ) : null}

        {vehicle && tab === 0 ? (
          <Box component="form" onSubmit={handleSaveInfo}>
            <Box className={classes.detailGrid}>
              <Detail label="Statut">
                {status ? <span className={`${classes.statusBadge} ${classes[status.badge]}`}>{status.label}</span> : statusLabel(vehicle.status)}
              </Detail>
              <Detail label="Emplacement">{locationName(vehicle.location)}</Detail>
              <Detail label="Coût de revient">{vehicle.costs?.landed_cost == null ? "-" : formatMoney(vehicle.costs.landed_cost)}</Detail>
              {vehicle.reserved_until ? <Detail label="Réservé jusqu'au">{formatDateTime(vehicle.reserved_until)}</Detail> : null}
            </Box>
            <Box className={`${classes.entryForm} ${classes.detailForm}`}>
              {INFO_FIELDS.map((field) => renderField(field, info, setInfo))}
            </Box>
            <Box className={classes.detailActions}>
              <Button className={classes.saveButton} type="submit" variant="contained" disabled={saving} startIcon={<SaveOutlinedIcon />}>
                {saving ? "Enregistrement..." : "Enregistrer les informations"}
              </Button>
            </Box>
          </Box>
        ) : null}

        {vehicle && tab === 1 ? (
          <Box component="form" onSubmit={handleSaveDocuments}>
            <Box className={missing.length ? classes.missingBox : classes.readyBox}>
              {missing.length ? (
                <>
                  <Typography className={classes.missingTitle}>Contrôles manquants avant la mise en vente</Typography>
                  <ul>{missing.map((item) => <li key={missingLabel(item)}>{missingLabel(item)}</li>)}</ul>
                </>
              ) : (
                <Typography className={classes.readyText}>Tous les contrôles sont complets : le véhicule peut être mis en vente.</Typography>
              )}
            </Box>
            <FormControlLabel
              control={<Checkbox size="small" checked={pdiPassed} onChange={(event) => setPdiPassed(event.target.checked)} />}
              label="Contrôle technique (PDI) validé"
            />
            <Box className={`${classes.entryForm} ${classes.detailForm}`}>
              {DOCUMENT_FIELDS.map((field) => renderField(field, documents, setDocuments))}
            </Box>
            <Box className={classes.detailActions}>
              <Button className={classes.saveButton} type="submit" variant="contained" disabled={saving} startIcon={<SaveOutlinedIcon />}>
                {saving ? "Enregistrement..." : "Enregistrer les contrôles"}
              </Button>
            </Box>
          </Box>
        ) : null}

        {vehicle && tab === 2 ? (
          historyLoading ? (
            <Box className={classes.dialogLoader}><CircularProgress size={28} /></Box>
          ) : history.length ? (
            <Box className={classes.timeline}>
              {history.map((movement, index) => (
                <Box key={movement.id || index} className={classes.timelineItem}>
                  <span className={classes.timelineDot} />
                  <Box>
                    <Typography className={classes.timelineTitle}>{movement.label || statusLabel(movement.to_status)}</Typography>
                    <Typography className={classes.timelineMeta}>
                      {formatDateTime(movement.date)}
                      {movement.actor_name ? ` · ${movement.actor_name}` : ""}
                    </Typography>
                    {movement.from_status || movement.to_status ? (
                      <Typography className={classes.timelineMeta}>
                        Statut : {statusLabel(movement.from_status)} → {statusLabel(movement.to_status)}
                      </Typography>
                    ) : null}
                    {movement.from_location || movement.to_location ? (
                      <Typography className={classes.timelineMeta}>
                        Emplacement : {locationName(movement.from_location)} → {locationName(movement.to_location)}
                      </Typography>
                    ) : null}
                    {movement.details ? (
                      <Typography className={classes.timelineMeta}>
                        {typeof movement.details === "string" ? movement.details : movement.details.reason || ""}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography className={classes.emptyText}>Aucun mouvement enregistré.</Typography>
          )
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default VehicleDetailsDialog;
